/**
 * @file Purchase PPD Type selection component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import withModal from '../global/modal/withModal';
import {
  PURCHASE_PPD_TYPE_MODAL,
  PURCHASE_PPD_PURCHASE_PACKAGE,
  PURCHASE_PPD_PURCHASE_INDIVIDUAL_PRINT,
} from '../../constants/modals';
import { PPD_CART_ITEM_TYPES } from '../../constants/ppd-purchase';
import Button from '../global/Button';
import { IMAGE_SIZE_CODE } from '../../constants/image-size-codes';
import DownloadPhotoButtonContainer from '../../containers/global/DownloadPhotoButtonContainer';
import Spinner from '../global/Spinner';
import { formatPriceForDisplay } from '../../util/format-money';
import {
  canPurchaseDownloadsAndPrints,
  getPpdDownloadPrice,
  hasSubscriptionInCart,
} from '../../util/ppd-purchase';
import Alert from '../global/Alert';

class PurchasePpdItemPurchaseModal extends Component {
  static propTypes = {
    actionProps: PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
        thumbnail_urls: PropTypes.arrayOf(
          PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
            url: PropTypes.string,
          }),
        ),
      }),
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
      album_print_packages: PropTypes.shape({
        total: PropTypes.number,
        record: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            print_package: PropTypes.shape({
              id: PropTypes.string,
              items: PropTypes.object,
            }),
          }),
        ),
      }),
    }),
    cart: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(PPD_CART_ITEM_TYPES)),
        photo: PropTypes.shape({
          id: PropTypes.string,
        }),
        printSize: PropTypes.shape({
          id: PropTypes.string,
        }),
        printPackage: PropTypes.shape({
          id: PropTypes.string,
        }),
        quantity: PropTypes.number,
      }),
    ),
    toggleCartItem: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    entitledPhotos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    user: PropTypes.shape({
      id: PropTypes.string,
      purchased_print_packages: PropTypes.shape({
        total: PropTypes.number,
      }),
    }),
    activeIdentity: PropTypes.shape({
      id: PropTypes.string,
    }),
  };

  static defaultProps = {
    cart: [],
    entitledPhotos: [],
  };

  state = {
    showRequirePackageError: false,
  };

  render() {
    const {
      actionProps: { photo },
      album,
      cart,
      toggleCartItem,
      onRequestClose,
      openModal,
      entitledPhotos,
      user,
      activeIdentity,
    } = this.props;

    const { showRequirePackageError } = this.state;

    if (!album) {
      return <Spinner />;
    }

    const hasPrintPackages = get(album, 'album_print_packages.total', 0) > 0;
    const isPhotosEntitled = entitledPhotos.find(({ id }) => id === photo.id);

    const isSelectedForDownload = cart.find(
      cartItem =>
        cartItem.type === PPD_CART_ITEM_TYPES.DOWNLOAD &&
        cartItem.photo.id === photo.id,
    );

    const photoPricing = get(album, 'photo_pricing');
    const downloadsPrice = getPpdDownloadPrice(photo, album);
    const hasPrintItems =
      get(photoPricing, 'print_items.records.length', 0) > 0;

    const requirePackagePurchase = get(
      photoPricing,
      'requires_package_purchase',
      false,
    );

    const downloadsPrintsEnabled = canPurchaseDownloadsAndPrints(
      cart,
      album,
      activeIdentity,
      user,
    );

    const hasSubscriptionSelected = hasSubscriptionInCart(
      cart,
      get(activeIdentity, 'id'),
    );

    let downloadButtonLabel = '';

    if (isSelectedForDownload) {
      downloadButtonLabel = 'Remove Hi-Res Download from cart';
    } else {
      downloadButtonLabel = `Hi-Res Download ${
        requirePackagePurchase ? 'Add-On' : 'Only'
      } ($${(formatPriceForDisplay(downloadsPrice), true)})`;
    }

    if (hasSubscriptionSelected) {
      downloadButtonLabel = 'Download Included with Photo Pass';
    }

    return (
      <div>
        <p>How would you like to purchase this photo?</p>
        {requirePackagePurchase && !downloadsPrintsEnabled && (
          <Alert type="info">
            To purchase individual prints or hi-res downloads, you must first
            select at least one package.
          </Alert>
        )}
        {showRequirePackageError && (
          <Alert type="warning">Please select a package first</Alert>
        )}
        {hasPrintPackages && (
          <Button
            onClick={() => {
              openModal(PURCHASE_PPD_PURCHASE_PACKAGE, { photo });
              onRequestClose();
            }}
            btnBlock
            id="printPackages-button"
          >
            Choose a Print Package
          </Button>
        )}
        {(downloadsPrice > 0 || isPhotosEntitled) && (
          <Fragment>
            {isPhotosEntitled ? (
              <DownloadPhotoButtonContainer
                photoId={photo.id}
                photoSize={IMAGE_SIZE_CODE.LARGE2X}
                includeWatermark={false}
                btnBlock
              >
                Download Hi-Res
              </DownloadPhotoButtonContainer>
            ) : (
              <Button
                onClick={() => {
                  if (hasSubscriptionSelected) {
                    return; // Do nothing
                  }

                  if (!downloadsPrintsEnabled) {
                    this.setState(() => ({
                      showRequirePackageError: true,
                    }));
                    return;
                  }

                  toggleCartItem(photo.id, PPD_CART_ITEM_TYPES.DOWNLOAD);
                }}
                className={
                  !downloadsPrintsEnabled || hasSubscriptionSelected
                    ? 'disabled'
                    : undefined
                }
                btnBlock
                id="download-button"
              >
                {downloadButtonLabel}
              </Button>
            )}
          </Fragment>
        )}
        {hasPrintItems && (
          <Button
            onClick={() => {
              if (!downloadsPrintsEnabled) {
                this.setState(() => ({ showRequirePackageError: true }));
                return;
              }

              openModal(PURCHASE_PPD_PURCHASE_INDIVIDUAL_PRINT, { photo });
              onRequestClose();
            }}
            btnBlock
            className={!downloadsPrintsEnabled ? 'disabled' : undefined}
            id="individual-prints-button"
          >
            Individual Print
          </Button>
        )}
        <Button
          onClick={onRequestClose}
          btnBlock
          btnStyle="secondary"
          className="text-center"
        >
          Continue Shopping
        </Button>
      </div>
    );
  }
}

export { PurchasePpdItemPurchaseModal as PurchasePpdItemPurchaseModalComp };

export default withModal(PurchasePpdItemPurchaseModal, {
  name: PURCHASE_PPD_TYPE_MODAL,
  size: 'sm',
  hideHeader: true,
});
